import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import { getUserGeolocation } from "@/fetch/ipGeolocation";
import { actions } from "@/store/searchSlice";
import { useGetPostalCodeDetailsQuery, useGetUserQuery, useLazyGetPrimaryShippingAddressQuery } from "@/store/userApi";
import { getCityAndZipcodeCookies, setCityAndZipcodeCookies, setRegionIdCookie } from "@/utils/locationDetails";

export function useLocationDetails() {
  const dispatch = useDispatch();
  const { data: user, isLoading: userIsLoading } = useGetUserQuery();
  const [getPrimaryShippingAddress, { data: primaryShippingAddress, isSuccess: isPrimaryShippingAddressSuccess }] =
    useLazyGetPrimaryShippingAddressQuery();

  const { city, zipCode } = getCityAndZipcodeCookies();
  const postalCodeDetails = useGetPostalCodeDetailsQuery(zipCode ?? "27513");

  const getGeoLocationDetails = useCallback(async () => {
    try {
      const { city: cookieCity, zipCode: cookieZipCode } = getCityAndZipcodeCookies();
      if (!cookieCity && !cookieZipCode) {
        const geoLocationDetails = await getUserGeolocation();
        if (geoLocationDetails) {
          const city = geoLocationDetails?.city;
          const zipCode = geoLocationDetails?.zipcode;
          setCityAndZipcodeCookies(city, zipCode);
        } else {
          setCityAndZipcodeCookies("Cary", "27513");
        }
      }
    } catch (error) {
      console.error("Error getting user geolocation:", error);
    }
  }, []);

  useEffect(() => {
    const shippingSubRegionId = postalCodeDetails.data?.shippingSubRegionId;
    if (shippingSubRegionId) {
      dispatch(actions.setShippingRegionId(shippingSubRegionId));
      setRegionIdCookie(shippingSubRegionId);
    }
  }, [dispatch, postalCodeDetails.data?.shippingSubRegionId]);

  const getLocationDetails = useCallback(async () => {
    try {
      const { city: cookieCity, zipCode: cookieZipCode } = getCityAndZipcodeCookies();
      if (user && !userIsLoading && !user?.IsGuestUser) {
        if (!cookieCity && !cookieZipCode) {
          const { data: primaryShippingAddress, isSuccess: isPrimaryShippingAddressSuccess } =
            await getPrimaryShippingAddress();
          if (primaryShippingAddress && isPrimaryShippingAddressSuccess && primaryShippingAddress?.Id !== null) {
            const city = primaryShippingAddress?.City;
            const zipCode = primaryShippingAddress?.PostalCD.slice(0, 5);
            setCityAndZipcodeCookies(city, zipCode);
          } else if (primaryShippingAddress !== undefined) {
            getGeoLocationDetails();
          }
        }
      } else if (user && !userIsLoading && user?.IsGuestUser) {
        getGeoLocationDetails();
      }
    } catch (error) {
      console.error("Error getting location details:", error);
    }
  }, [
    getGeoLocationDetails,
    getPrimaryShippingAddress,
    isPrimaryShippingAddressSuccess,
    primaryShippingAddress,
    user,
    userIsLoading,
  ]);

  useEffect(() => {
    getLocationDetails();
  }, [getLocationDetails]);

  return { city, zipCode };
}
